/**
 * @typedef {import("preact/compat").JSX.TargetedEvent<HTMLFormElement, Event>} QueryFormEvent
 * @typedef {import("src/services/ot-api.js").CheckAvailabilityBody} CheckAvailabilityBody
 * @typedef {import("src/modules/reservations/opentable.js").OTAvailableTime} OTAvailableTime
 */
import { useEffect, useRef } from "preact/compat";
import { html } from "htm/preact";
import { batch, Signal, useComputed, useSignal } from "@preact/signals";
import { useAppContext } from "src/services/context";
import { createDateFromInput, formatInputDate } from "src/services/helpers.js";
import { fetchOTTimes } from "src/services/ot-api.js";
import BaseButton from "../button/base-button";
import QueryFormSimpleRadio from "./query-form-simple-radio";
import {
    formatSeating,
    generatePartyOptions,
    generateTimeOptions,
	getDefaultDate,
	getDefaultTime
} from "./query-form-utils.js";
import FieldBox from "../input-field-box/input-field-box";
import settings from "src/services/settings.js";
import { decode } from "he";

const svgArrowDownIcon = `<svg viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.6133 11.0596L21.1152 1.55762L20.4114 0.853769L11.0479 10.2173L10.0523 10.2173L0.703638 0.853768L-0.000210264 1.55762L9.50174 11.0596C9.64031 11.1983 9.80489 11.3084 9.98605 11.3836C10.1672 11.4587 10.3614 11.4973 10.5575 11.4973C10.7536 11.4973 10.9478 11.4587 11.129 11.3836C11.3101 11.3084 11.4747 11.1983 11.6133 11.0596Z" fill="#173F35" stroke="#173F35" stroke-width="1.5"></path></svg>`;

// TODO: Add a state that tracks if there is a locked reservation

/**
 * @typedef {object} QueryFormProps
 * @prop {boolean} isVisible
 * @param {QueryFormProps} props
 * @returns {import("preact").VNode}
 */
function QueryForm(props) {
    const state = useAppContext();
    const isLoading = useSignal(false);

    const selectedTimeValue = useSignal("");
    const selectedPartyValue = useSignal("");
    const selectedDateValue = useSignal("");
    const timesRef = useRef(null);
    const seatingRef = useRef(null);

    const confirmReady = useComputed(() => {
        return !!(state.selectedTime.value && state.selectedSeating.value);
    });

    /** @type Signal<OTAvailableTime[]> */
    const times = useSignal([]);
    const errorMessage = useSignal("");
    // Organizes the OT times into a multistep time -> seating selection
    const timesConsolidated = useComputed(() => {
        return times.value.reduce((memo, time) => {
            if (memo[time.time]) {
                memo[time.time].push(time);
            } else {
                memo[time.time] = [time];
            }
            return memo;
        }, {});
    });
    /** @type Signal<OTAvailableTime[]> */
    const selectedTime = useSignal(null);

    /** @param {string} value */
    const handlePartySizeChange = (value) => {
        selectedPartyValue.value = value;
        state.partySize.value = Number(value);

        times.value = [];
        selectedTime.value = null;
        state.selectedTime.value = "";
        state.selectedSeating.value = "";
        state.isNextStepVisible.value = false;
    };

    /** @param {Event & { detail: string }} value */
    const handleDateChange = (value) => {
        const dateString = value.detail;
        const selectedDate = createDateFromInput(dateString);
        
        const formattedDate = formatInputDate(selectedDate);
        selectedDateValue.value = formattedDate;
        
        const nextDate = new Date(state.preferredDate.value.getTime());
        nextDate.setMonth(selectedDate.getMonth());
        nextDate.setFullYear(selectedDate.getFullYear());
        nextDate.setDate(selectedDate.getDate());

        state.preferredDate.value = nextDate;

        times.value = [];
        selectedTime.value = null;
        state.selectedTime.value = "";
        state.selectedSeating.value = "";
        state.isNextStepVisible.value = false;
    };

    /** @param {string} value */
    const handleTimeChange = (value) => {
        selectedTimeValue.value = value;

        const [hour, minute] = value.split(":").map(Number);
        const nextDate = new Date(state.preferredDate.value);
        nextDate.setHours(hour);
        nextDate.setMinutes(minute);
        nextDate.setSeconds(0);

        state.preferredDate.value = nextDate;

        times.value = [];
        selectedTime.value = null;
        state.selectedTime.value = "";
        state.selectedSeating.value = "";
        state.isNextStepVisible.value = false;
    };

    /**
     * @param {import("preact").JSX.TargetedSubmitEvent<HTMLFormElement>} event
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        errorMessage.value = "";
        try {
            /** @type CheckAvailabilityBody */
            const payload = {
                partySize: state.partySize.value,
                rid: state.selectedLocationRid.value,
                startDateTime:
                    formatInputDate(state.preferredDate.value) +
                    "T" +
                    encodeURIComponent(
                        state.preferredDate.value.toLocaleTimeString([], {
                            hour12: false,
                            hour: "2-digit",
                            minute: "2-digit",
                        })
                    ),
            };
            isLoading.value = true;
            const json = await fetchOTTimes(payload);
            isLoading.value = false;
            if (json.errors?.length > 0) {
                throw json.errors[0];
            }
            if (json.times_available) {
                times.value = json.times_available;
            }
        } catch (err) {
            errorMessage.value =
                "We could not find availability online for your party time/size, please call the restaurant directly for more options.";
            if (typeof err?.message === "string") {
                if (err.message.toLowerCase().includes("oauth")) {
                    errorMessage.value = `No date and time found. Please call our location directly at (${state.selectedRestaurant.value?.acf?.phone_number}) and we will be happy to help you!`;
                }
            }
            isLoading.value = false;
        }
    };

    /**
     * @param {Event & { target: HTMLInputElement, currentTarget: HTMLFormElement }} event
     */
    const handleChange = (event) => {
        const formData = new FormData(event.currentTarget);

        if (event.target.name === "selected_time") {
            const raw = timesConsolidated.value[event.target.value];

            selectedTime.value = Array.from(
                new Map(raw.map((l) => [l.attribute, l])).values()
            );
        }

        const selectedTimeValue = formData.get("selected_time")?.toString();
        const selectedSeatingValue = formData
            .get("reservation_attribute")
            ?.toString();

        if (!selectedTimeValue || !selectedSeatingValue) return;

        const selectedTimeDateValue = new Date(Date.parse(selectedTimeValue));

        batch(() => {
            state.selectedTime.value = selectedTimeValue;
            state.selectedTimeDate.value = selectedTimeDateValue;
            state.selectedSeating.value = selectedSeatingValue;
        });
    };

    const handleCancel = () => {
        batch(() => {
            state.pendingTimer.value = null;
            state.lockedReservation.value = null;
            state.reservationToken.value = "";
            state.selectedTime.value = "";
            state.selectedSeating.value = "";
            state.isNextStepVisible.value = false;
        });
    };

    const handleNext = () => {
        const selectedSeatingValue = state.selectedSeating.value;
        const experienceList = timesConsolidated.value[
            selectedTime.value?.[0]?.time
        ]
            ?.filter(
                (time) =>
                    time.attribute === selectedSeatingValue &&
                    time.availabilityType?.includes("Experience")
            )
            ?.flatMap((time) => time.experiences || []) || [];

        batch(() => {
            state.experienceIDs.value = experienceList;
            state.isNextStepVisible.value = true;
        });
    };

    useEffect(() => {
        if (!selectedTime.value) return;
      
        requestAnimationFrame(() => {
          seatingRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        });
      }, [selectedTime.value]);
    
      useEffect(() => {
        if (!Object.keys(timesConsolidated.value).length) return;
    
        requestAnimationFrame(() => {
            timesRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
    
            const firstRadio = timesRef.current?.querySelector(
                'input[name="selected_time"]'
            );
    
            firstRadio?.focus();
        });
    }, [times.value]);

      const nextButtonRef = useRef(null);

        useEffect(() => {
        if (!confirmReady.value) return;

        requestAnimationFrame(() => {
            nextButtonRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
            });
        });
        }, [confirmReady.value]);

    useEffect(() => {
        if (props.isVisible) {
            times.value = [];
            selectedTime.value = null;
            batch(() => {
                state.selectedTime.value = "";
                state.selectedSeating.value = "";
                state.isNextStepVisible.value = false;
            });
        }
    }, [props.isVisible]);

    useEffect(() => {
        // preset defaults
        selectedPartyValue.value = "2";
    
        const defaultDate = getDefaultDate();
        const defaultTime = getDefaultTime();
    
        selectedDateValue.value = defaultDate;
        selectedTimeValue.value = defaultTime;
    
        state.partySize.value = 2;
    
        const selectedDate = createDateFromInput(defaultDate);
        const [hour, minute] = defaultTime.split(":").map(Number);
    
        const nextDate = new Date(selectedDate);
        nextDate.setHours(hour, minute, 0, 0);
    
        state.preferredDate.value = nextDate;
    }, []);


    return html`
      <div class="mx-reservation-form__step reservation-form-wrapper ${props.isVisible ? "mx-reservation-form__step--active" : ""}">
    
        <!-- HEADER (TABS ONLY) -->
        <header class="mx-reservation-form__header">
                <div
                class="form-section-switcher"
                aria-label="Reservation progress: Date step"
            >
                <span
                class="form-section-option"
                aria-current="step"
                >
                Date
                <span class="form-section-option__indicator"></span>
                </span>
            
                <span
                class="form-section-option"
                aria-hidden="true"
                >
                Details
                <span class="form-section-option__indicator"></span>
                </span>
            </div>
    
            ${errorMessage.value
                ? html`
                    <p
                      class="mx-form__unavailable-text"
                      role="alert"
                    >
                      ${errorMessage.value}
                    </p>
                  `
                : null}
        </header>
    
        <!-- SCROLL CONTAINER -->
        <div class="moxies-opentable-content">
    
          <!-- TITLE -->
          <div class="moxies-title">
            <h4 class="elementor-heading-title moxies-heading">
              <span class="moxies-title-default">Moxies</span>
              <span class="moxies-italic moxies-span">
                ${decode(state.selectedRestaurant.value.title.rendered)}
              </span>
            </h4>
          </div>
    
          <!-- ADDRESS -->
          <div class="mx-reservation-form__address">
            ${state.selectedRestaurant.value.acf.street_address}
          </div>
    
          <!-- FORM -->
          <form
            id="opentable"
            onSubmit=${handleSubmit}
            onChange=${handleChange}
            class="mx-form"
            aria-label="Reservation details"
          >
    
            <!-- FIELDS --> 
            <${FieldBox}
              id="party-size"
              label="Number of People"
              inlineSvg=${svgArrowDownIcon}
              onChange=${handlePartySizeChange}
              options=${generatePartyOptions()}
              value=${selectedPartyValue.value || "2"}
            />

                <div class="mx-group-booking-notice">
                    For groups of 11 or more, please submit an inquiry through our <a href="${state.selectedRestaurant.value.link}group-bookings/">Group Bookings</a> page and someone from our team will get back to you shortly.
                </div>
            <${FieldBox}
              id="choose-date"
              label="Choose Date"
              type="date"
              inlineSvg=${svgArrowDownIcon}
              onChange=${handleDateChange}
              value=${selectedDateValue.value || getDefaultDate()}
            />
    
            <${FieldBox}
              id="choose-time"
              label="Choose Time"
              type="select"
              inlineSvg=${svgArrowDownIcon}
              onChange=${handleTimeChange}
              options=${generateTimeOptions(state.preferredDate.value)}
              value=${selectedTimeValue.value || getDefaultTime()}
            />
    
            <!-- LOADING / TIMES HEADER -->
            ${Object.keys(timesConsolidated.value).length > 0
              ? html`
                  <div class="mx-query__times-title">
                    ${isLoading.value
                      ? html`
                          <span class="mx-query__locking">
                            <mx-spinner></mx-spinner>
                            Securing your table...
                          </span>
                        `
                      : null}
                  </div>
                `
              : null}
    
            <!-- SUBMIT BUTTON -->
            ${selectedPartyValue.value &&
            selectedDateValue.value &&
            selectedTimeValue.value
              ? html`
                  <div class="find-a-time-moxies-button" ref=${nextButtonRef}>
                    <${BaseButton}
                      classes=${["find-a-time-moxies-button__bg"]}
                      type="submit"
                      isLoading=${isLoading.value}
                      size="lg"
                    >
                      AVAILABLE TABLES
                    </${BaseButton}>
                  </div>
                `
              : null}
    
            <!-- TIMES -->
            <div                
                class="mx-container-form__times"
                ref=${timesRef}
                aria-live="polite"
                aria-atomic="true"
            >
              ${Object.keys(timesConsolidated.value).length > 0
                ? html`
                <fieldset class="mx-times-fieldset">
                <legend class="reservation-seating__title">
                    Available Times
                </legend>
    
                    ${Object.keys(timesConsolidated.value).map(
                      (t) => html`
                        <div class="mx-time-option">
                          <${(QueryFormSimpleRadio)}
                            key=${t}
                            disabled=${isLoading.value}
                            name="selected_time"
                            value=${t}
                          >
                            ${new Date(t)
                              .toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })
                              .toUpperCase()}
                          </${(QueryFormSimpleRadio)}>
                        </div>
                      `
                    )}
                    </fieldset>
                  `
                : null}
            </div>
    
            <!-- SEATING -->
            <div class="mx-container-form__seating" ref=${seatingRef}>
            ${selectedTime.value
              ? html`
                <fieldset class="mx-seating-fieldset">
                  <legend class="reservation-seating__title">
                    Seating Type
                  </legend>
          
                  ${selectedTime.value.map(
                    (l) => html`
                      <div class="mx-seating-options">
                        <${QueryFormSimpleRadio}
                          key=${`${l.time}-${l.attribute}`}
                          disabled=${isLoading.value}
                          name="reservation_attribute"
                          value=${l.attribute}
                        >
                          ${formatSeating(l.attribute)}
                        </${QueryFormSimpleRadio}>
                      </div>
                    `
                  )}
                </fieldset>
              `
              : null}
          </div>
    
            <!-- NEXT BUTTON -->
            ${confirmReady.value
              ? html`
                  <div class="find-a-time-moxies-button">
                    <${BaseButton}
                      classes=${["find-a-time-moxies-button__bg"]}
                      size="lg"
                      disabled=${isLoading.value}
                      onClick=${handleNext}
                    >
                      NEXT
                    </${BaseButton}>
                  </div>
                `
              : null}
    
          </form>
        </div>
      </div>
    `;
    
}

export default QueryForm;
